import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { RGBShiftShader } from "three/examples/jsm/shaders/RGBShiftShader.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import GALLERYv from "../shaders/galleryVert.glsl";
import GALLERYf from "../shaders/galleryFrag.glsl";

export function createScene({ renderer, camera, loadingManager } = {}) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);

  const composer = new EffectComposer(renderer);

  composer.addPass(new RenderPass(scene, camera));

  const chromatic = new ShaderPass(RGBShiftShader);
  chromatic.uniforms.amount.value = 0.0015;
  composer.addPass(chromatic);
  const outputPass = new OutputPass();
  composer.addPass(outputPass);

  const customCursor =
    typeof document !== "undefined" && document.querySelector(".customX");
  let isOverUI = false;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isCoarse =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(ua) || isCoarse;

  const CONFIG = Object.freeze({
    FRAY_STRENGTH_DESKTOP: 0.9,
    FRAY_STRENGTH_MOBILE: 0.35,
    CAROUSEL_ZOOM_DESKTOP: 1.5,
    CAROUSEL_ZOOM_MOBILE: 1.5,
    CAROUSEL_PLANE_SCALE_DESKTOP: 1.0,
    CAROUSEL_PLANE_SCALE_MOBILE: 0.32,
    CAROUSEL_GAP_DESKTOP: 2.1,
    CAROUSEL_GAP_MOBILE: 0.7,
    GALLERY_PLANE_SCALE_DESKTOP: 1.0,
    GALLERY_PLANE_SCALE_MOBILE: 0.65,
    GALLERY_PLANE_GAP_DESKTOP: 5.0,
    GALLERY_PLANE_GAP_MOBILE: 3.2,
    TOP_LINE: 0.18,
    BOTTOM_LINE: 0.82,
  });

  const FRAY_STRENGTH = isMobile
    ? CONFIG.FRAY_STRENGTH_MOBILE
    : CONFIG.FRAY_STRENGTH_DESKTOP;
  const CAROUSEL_ZOOM = isMobile
    ? CONFIG.CAROUSEL_ZOOM_MOBILE
    : CONFIG.CAROUSEL_ZOOM_DESKTOP;
  const CAROUSEL_PLANE_SCALE =
    (isMobile
      ? CONFIG.CAROUSEL_PLANE_SCALE_MOBILE
      : CONFIG.CAROUSEL_PLANE_SCALE_DESKTOP) * CAROUSEL_ZOOM;
  const CAROUSEL_PLANE_POS_X = 0;
  const CAROUSEL_PLANE_POS_Y = isMobile ? -0.5 : 0;
  const CAROUSEL_GAP =
    (isMobile ? CONFIG.CAROUSEL_GAP_MOBILE : CONFIG.CAROUSEL_GAP_DESKTOP) *
    CAROUSEL_ZOOM;
  const GALLERY_PLANE_SCALE = isMobile
    ? CONFIG.GALLERY_PLANE_SCALE_MOBILE
    : CONFIG.GALLERY_PLANE_SCALE_DESKTOP;
  const GALLERY_PLANE_GAP = isMobile
    ? CONFIG.GALLERY_PLANE_GAP_MOBILE
    : CONFIG.GALLERY_PLANE_GAP_DESKTOP;

  const MEDIA_ASPECT = 16 / 9;

  const CAROUSEL_PLANE_WIDTH = 4.5 * CAROUSEL_PLANE_SCALE;
  const CAROUSEL_PLANE_HEIGHT = CAROUSEL_PLANE_WIDTH / MEDIA_ASPECT;

  const GALLERY_PLANE_WIDTH = 4.5 * GALLERY_PLANE_SCALE;
  const GALLERY_PLANE_HEIGHT = GALLERY_PLANE_WIDTH / MEDIA_ASPECT;

  const planeGeometry = new THREE.PlaneGeometry(
    CAROUSEL_PLANE_WIDTH,
    CAROUSEL_PLANE_HEIGHT,
    isMobile ? 32 : 48,
    isMobile ? 16 : 24,
  );

  const galleryGeometry = new THREE.PlaneGeometry(
    GALLERY_PLANE_WIDTH,
    GALLERY_PLANE_HEIGHT,
    32,
    32,
  );

  let viewState = "carousel";
  const galleryGroup = new THREE.Group();
  scene.add(galleryGroup);

  let carouselOpacity = 1.0;
  let galleryOpacity = 0.0;

  let resolveReady;
  const ready = new Promise((res) => (resolveReady = res));

  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2(0, 0);
  const tmpVec2 = new THREE.Vector2();

  const planes = [];
  let cachedCarouselMeshes = null;

  let mouseVelocity = 0;
  let targetY = 0,
    currentY = 0;
  let targetX = 0,
    currentX = 0;
  let targetDelta = 0,
    currentDelta = 0;
  let targetDeltaX = 0,
    currentDeltaX = 0;

  const topLine = CONFIG.TOP_LINE;
  const bottomLine = CONFIG.BOTTOM_LINE;

  const textureMap = new Map();
  const loader = new THREE.TextureLoader(loadingManager);
  const videoElementMap = new Map();

  const descPanel =
    typeof document !== "undefined" &&
    document.getElementById("gallery-desc-panel");
  const descTitle =
    typeof document !== "undefined" &&
    document.getElementById("gallery-desc-title");
  const descBody =
    typeof document !== "undefined" &&
    document.getElementById("gallery-desc-body");

  const assets = import.meta.glob("../../assets/*", {
    eager: true,
    import: "default",
  });
  function asset(name) {
    return assets[`../../assets/${name}`];
  }

  let projectData = [];
  try {
    const tEl =
      typeof document !== "undefined" &&
      document.getElementById("gallery-translations");
    if (tEl && tEl.textContent.trim()) {
      const localized = JSON.parse(tEl.textContent);
      projectData = localized.map((p) => {
        const isVid = (name) => /\.(mp4|webm|mov|m4v)$/i.test(name || "");
        const mainFile = asset(`${p.main}.mp4`) ?? asset(`${p.main}.webp`);
        return {
          main: mainFile,
          gallery: p.gallery.map(
            (g) => asset(`${g}.mp4`) ?? asset(`${g}.webp`),
          ),
          title: p.title,
          desc: p.desc,
        };
      });
    }
  } catch (e) {
    console.warn("💩: błąd parsowania JSON", e);
  }

  const urls = projectData.map((p) => p.main);

  function showDescription(projectIndex) {
    const pd = projectData[projectIndex];
    if (!pd || !pd.desc || !descPanel || !descTitle || !descBody) {
      hideDescription();
      return;
    }
    descTitle.textContent = pd.title || `Projekt ${projectIndex + 1}`;
    descBody.textContent = pd.desc;
    gsap.killTweensOf(descPanel);
    descPanel.hidden = false;
    gsap.fromTo(
      descPanel,
      { opacity: 0, x: 20 },
      { opacity: 1, x: 0, duration: 0.45, ease: "power2.out" },
    );
  }

  function hideDescription() {
    if (!descPanel) return;
    gsap.killTweensOf(descPanel);
    gsap.to(descPanel, {
      opacity: 0,
      x: 20,
      duration: 0.35,
      ease: "power2.in",
      onComplete: () => {
        descPanel.hidden = true;
      },
    });
  }

  function isVideo(url) {
    return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
  }

  function createVideoTexture(url) {
    if (videoElementMap.has(url)) {
      const existing = videoElementMap.get(url);
      return { video: existing, tex: new THREE.VideoTexture(existing) };
    }
    const video = document.createElement("video");
    video.src = url;
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "auto";
    const tex = new THREE.VideoTexture(video);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    videoElementMap.set(url, video);
    return { video, tex };
  }

  function playVideo(url) {
    const v = videoElementMap.get(url);
    if (!v) return;
    if (v.__playing) return;
    v.__playing = true;
    const p = v.play();
    if (p && p.catch)
      p.catch(() => {
        v.__playing = false;
      });
  }

  function pauseVideo(url) {
    const v = videoElementMap.get(url);
    if (!v) return;
    if (!v.__playing) return;
    try {
      v.pause();
    } catch (e) {}
    v.__playing = false;
  }

  function getUniforms(texture, height, isVideo = false) {
    const texW = texture.image
      ? texture.image.videoWidth ||
        texture.image.naturalWidth ||
        texture.image.width ||
        1
      : 1;
    const texH = texture.image
      ? texture.image.videoHeight ||
        texture.image.naturalHeight ||
        texture.image.height ||
        1
      : 1;
    return {
      uTexture: { value: texture },
      uTexSize: { value: new THREE.Vector2(texW, texH) },
      uIsVideo: { value: isVideo ? 1.0 : 0.0 },
      uTime: { value: 0 },
      uScrollOffset: { value: 0 },
      uMousePos: { value: new THREE.Vector2(0, 0) },
      uMovementStrength: { value: 0 },
      uHover: { value: 0 },
      uTopLine: { value: topLine },
      uBottomLine: { value: bottomLine },
      uPlaneY: { value: 0 },
      uPlaneHeight: { value: height },
      uOpacity: { value: 1.0 },
      uEdgeVelocity: { value: new THREE.Vector2(0, 0) },
      uIsMobile: { value: isMobile ? 1.0 : 0.0 },
      uEntryProgress: { value: 0.0 },
    };
  }

  function startLoading() {
    const allUrls = [
      ...new Set(projectData.flatMap((p) => [p.main, ...p.gallery])),
    ];
    let loaded = 0;
    const total = allUrls.length;
    const checkDone = () => {
      loaded++;
      if (loaded === total) buildPlanes();
    };
    allUrls.forEach((url) => {
      loadingManager.itemStart(url);
      if (isVideo(url)) {
        const { video, tex } = createVideoTexture(url);
        const onReady = () => {
          if (!textureMap.has(url)) textureMap.set(url, tex);
          loadingManager.itemEnd(url);
          cleanup();
          checkDone();
        };
        const onErr = (err) => {
          console.error("💩 Błąd ładowania wideo:", url, err);
          loadingManager.itemEnd(url);
          cleanup();
          checkDone();
        };
        function cleanup() {
          video.removeEventListener("canplaythrough", onReady);
          video.removeEventListener("loadedmetadata", onReady);
          video.removeEventListener("error", onErr);
        }
        video.addEventListener("canplaythrough", onReady, { once: true });
        video.addEventListener("loadedmetadata", onReady, { once: true });
        video.addEventListener("error", onErr, { once: true });
      } else {
        loader.load(
          url,
          (tex) => {
            tex.minFilter = THREE.LinearMipmapLinearFilter;
            tex.magFilter = THREE.LinearFilter;
            tex.generateMipmaps = true;
            tex.colorSpace = THREE.SRGBColorSpace;
            tex.anisotropy = Math.min(
              renderer.capabilities.getMaxAnisotropy(),
              16,
            );
            textureMap.set(url, tex);
            loadingManager.itemEnd(url);
            checkDone();
          },
          undefined,
          (err) => {
            console.error("💩 Błąd ładowania obrazu:", url, err);
            loadingManager.itemEnd(url);
            checkDone();
          },
        );
      }
    });
  }

  function buildPlanes() {
    const baseMat = new THREE.ShaderMaterial({
      vertexShader: GALLERYv,
      fragmentShader: GALLERYf,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
      depthTest: true,
    });
    for (let i = 0; i < projectData.length; i++) {
      const data = projectData[i];
      const tex = textureMap.get(data.main) || new THREE.Texture();
      const geometry = planeGeometry;
      const meshMat = baseMat.clone();

      meshMat.uniforms = getUniforms(
        tex,
        geometry.parameters.height,
        isVideo(data.main),
      );

      const meshObj = new THREE.Mesh(geometry, meshMat);
      const group = new THREE.Group();
      group.add(meshObj);
      group.position.x = CAROUSEL_PLANE_POS_X;
      group.position.y = CAROUSEL_PLANE_POS_Y - i * CAROUSEL_GAP;
      scene.add(group);
      group.userData = {
        baseY: -i * CAROUSEL_GAP,
        targetMouse: new THREE.Vector2(0, 0),
        currentMouse: new THREE.Vector2(0, 0),
        hoverVal: 0,
        originalIndex: i,
        galleryUrls: data.gallery,
        prevY: undefined,
        edgeVelY: 0,
        isVideo: isVideo(data.main),
        videoUrl: isVideo(data.main) ? data.main : null,
        videoPlaying: false,
      };
      planes.push(group);
    }
    refreshCarouselMeshCache();
    resolveReady();
  }

  function refreshCarouselMeshCache() {
    const arr = new Array(planes.length);
    for (let i = 0; i < planes.length; i++) arr[i] = planes[i].children[0];
    cachedCarouselMeshes = arr.filter(Boolean);
  }

  function openGallery(clickedGroup) {
    targetX = 0;
    currentX = 0;
    targetDeltaX = 0;
    currentDeltaX = 0;
    document.body.classList.add("is-gallery-mode");
    const indicator = document.querySelector(".stickyScroll");
    const sceneIndicator = document.querySelector(".scene-indicator");

    if (indicator)
      gsap.to(indicator, {
        rotation: isMobile ? 90 : 0,
        duration: 0.45,
        ease: "power3.inOut",
        transformOrigin: "50% 50%",
      });
    if (sceneIndicator) {
      gsap.to(sceneIndicator, {
        opacity: 0,
        pointerEvents: "none",
        duration: 0.45,
        ease: "power2.out",
      });
    }
    viewState = "gallery";
    window.setCarouselActive && window.setCarouselActive(false);
    const galleryUrls = clickedGroup.userData.galleryUrls;
    const projectIndex = clickedGroup.userData.originalIndex;
    while (galleryGroup.children.length > 0) {
      const child = galleryGroup.children[0];
      if (child.material) {
        child.material.dispose();
      }
      galleryGroup.remove(child);
    }
    showDescription(projectIndex);
    const baseGalleryMaterial = new THREE.ShaderMaterial({
      vertexShader: GALLERYv,
      fragmentShader: GALLERYf,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      defines: { USE_HORIZONTAL_ARC: "", CLEAN_MODE: "" },
    });
    for (let i = 0; i < galleryUrls.length; i++) {
      const url = galleryUrls[i];
      const tex = textureMap.get(url) || new THREE.Texture();
      const meshMat = baseGalleryMaterial.clone();
      meshMat.uniforms = getUniforms(
        tex,
        galleryGeometry.parameters.height,
        isVideo(url),
      );

      meshMat.uniforms.uScrollOffset.value = 0;
      meshMat.uniforms.uTopLine.value = -1.0;
      meshMat.uniforms.uBottomLine.value = 2.0;
      meshMat.uniforms.uOpacity.value = 0.0;
      const mesh = new THREE.Mesh(galleryGeometry, meshMat);
      mesh.position.x =
        (i - Math.floor(galleryUrls.length / 2)) * GALLERY_PLANE_GAP;
      mesh.position.y = 0;
      galleryGroup.add(mesh);
      if (isVideo(url)) playVideo(url);
      mesh.userData = { url };
    }
  }

  function closeGallery() {
    targetX = 0;
    currentX = 0;
    targetDeltaX = 0;
    currentDeltaX = 0;
    const indicator = document.querySelector(".stickyScroll");
    document.body.classList.remove("is-gallery-mode");
    if (indicator)
      gsap.to(indicator, {
        rotation: 0,
        duration: 0.45,
        ease: "power3.inOut",
        transformOrigin: "50% 50%",
      });
    for (let i = 0; i < galleryGroup.children.length; i++) {
      const u = galleryGroup.children[i].userData;
      if (u?.url && isVideo(u.url)) pauseVideo(u.url);
    }
    hideDescription();
    viewState = "carousel";
    window.setCarouselActive && window.setCarouselActive(true);
  }

  function onClick(e) {
    const clickMouse = tmpVec2;
    clickMouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    clickMouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(clickMouse, camera);
    if (viewState === "carousel") {
      if (!cachedCarouselMeshes || cachedCarouselMeshes.length === 0) return;
      const intersects = raycaster.intersectObjects(
        cachedCarouselMeshes,
        false,
      );
      if (intersects.length > 0) {
        const parentGroup = intersects[0].object.parent;
        openGallery(parentGroup);
      }
    } else {
      closeGallery();
    }
  }

  function onWheel(e) {
    if (viewState === "carousel") {
      targetY += e.deltaY * 0.005;
      targetDelta = e.deltaY * 0.025;
    } else if (viewState === "gallery") {
      targetX += e.deltaY * 0.01;
      targetDeltaX = e.deltaY * 0.02;
    }
  }

  let touchLastY = 0,
    touchLastX = 0;
  function onTouchStart(e) {
    if (e.touches && e.touches[0]) {
      touchLastY = e.touches[0].clientY;
      touchLastX = e.touches[0].clientX;
    }
  }
  function onTouchMove(e) {
    if (!e.touches || !e.touches[0]) return;
    const currentTouchX = e.touches[0].clientX;
    const currentTouchY = e.touches[0].clientY;
    if (viewState === "carousel") {
      const delta = touchLastY - currentTouchY;
      targetY -= delta * 0.005;
      targetDelta = -delta * 0.8;
      touchLastY = currentTouchY;
    } else if (viewState === "gallery") {
      const delta = touchLastX - currentTouchX;
      targetX += delta * 0.01;
      targetDeltaX = delta * 0.6;
      touchLastX = currentTouchX;
    }
  }

  let lastPointerForVel = 0;
  function onPointerMove(e) {
    mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
  }
  function onPointerMoveForVelocity(e) {
    const now = performance.now();
    if (now - lastPointerForVel < 12) return;
    lastPointerForVel = now;
    const nx = (e.clientX / window.innerWidth) * 2 - 1;
    const ny = -(e.clientY / window.innerHeight) * 2 + 1;
    const dx = nx - mouse.x;
    const dy = ny - mouse.y;
    mouseVelocity += Math.sqrt(dx * dx + dy * dy) * 0.5;
    mouse.x = nx;
    mouse.y = ny;
  }

  const margin = CAROUSEL_GAP;
  const totalHeight = projectData.length * CAROUSEL_GAP;
  let elapsed = 0;

  function update(dt = 1 / 60) {
    elapsed += dt;
    mouseVelocity *= 0.9;
    if (viewState === "carousel")
      chromatic.uniforms.amount.value = 0.0015 + Math.abs(currentDelta) * 0.002;
    else
      chromatic.uniforms.amount.value +=
        (0.0 - chromatic.uniforms.amount.value) * 0.1;
    const animSpeed = 0.05;
    if (viewState === "carousel") {
      carouselOpacity += (1.0 - carouselOpacity) * animSpeed;
      galleryOpacity += (0.0 - galleryOpacity) * animSpeed;
    } else {
      carouselOpacity += (0.0 - carouselOpacity) * animSpeed;
      galleryOpacity += (1.0 - galleryOpacity) * animSpeed;
    }
    if (viewState === "carousel") {
      currentY += (targetY - currentY) * 0.1;
      targetDelta *= 0.9;
      currentDelta += (targetDelta - currentDelta) * 0.1;
    } else {
      currentDelta += (0 - currentDelta) * 0.1;
    }
    let intersects = [];
    if (!isOverUI) {
      raycaster.setFromCamera(mouse, camera);
      if (
        carouselOpacity > 0.01 &&
        cachedCarouselMeshes &&
        cachedCarouselMeshes.length
      )
        intersects = raycaster.intersectObjects(cachedCarouselMeshes, false);
      if (customCursor) {
        if (intersects.length > 0) customCursor.classList.add("hovered");
        else customCursor.classList.remove("hovered");
      }
    } else if (customCursor) customCursor.classList.remove("hovered");
    for (let i = 0; i < planes.length; i++) {
      const group = planes[i];
      if (!group) continue;
      let y = group.userData.baseY - currentY;
      const movement = Math.min(
        1.0,
        Math.abs(currentDelta) * 2.0 * FRAY_STRENGTH,
      );
      y =
        ((((y + totalHeight / 2) % totalHeight) + totalHeight) % totalHeight) -
        totalHeight / 2;
      group.position.y = y;
      const centerPriority = Math.max(
        0,
        1000 - Math.round(Math.abs(group.position.y) * 100),
      );
      group.renderOrder = centerPriority;
      const meshChild = group.children[0];
      const hit = intersects.find((it) => it.object === meshChild);
      if (hit) {
        const uv = hit.uv || tmpVec2.set(0.5, 0.5);
        group.userData.targetMouse.set(uv.x - 0.5, uv.y - 0.5);
        const movementBoost = Math.min(1.0, mouseVelocity * 1.5);
        const hoverTarget = 0.6 + 0.4 * movementBoost;
        group.userData.hoverVal +=
          (hoverTarget - group.userData.hoverVal) * 0.18;
      } else {
        group.userData.hoverVal += (0 - group.userData.hoverVal) * 0.18;
      }
      group.userData.currentMouse.x +=
        (group.userData.targetMouse.x - group.userData.currentMouse.x) * 0.15;
      group.userData.currentMouse.y +=
        (group.userData.targetMouse.y - group.userData.currentMouse.y) * 0.15;
      if (group.userData.prevY === undefined)
        group.userData.prevY = group.position.y;
      const dy = (group.position.y - group.userData.prevY) / dt;
      group.userData.prevY = group.position.y;
      const planeHeight =
        meshChild &&
        meshChild.material &&
        meshChild.material.uniforms &&
        meshChild.material.uniforms.uPlaneHeight
          ? meshChild.material.uniforms.uPlaneHeight.value || 1.0
          : 1.0;
      const velUVY = dy / planeHeight;
      const smooth = 0.85;
      group.userData.edgeVelY =
        (group.userData.edgeVelY || 0) * smooth + velUVY * (1.0 - smooth);
      if (group.userData.isVideo && group.userData.videoUrl) {
        const nearCenter = Math.abs(group.position.y) < 1.5;
        if (nearCenter && !group.userData.videoPlaying) {
          playVideo(group.userData.videoUrl);
          group.userData.videoPlaying = true;
        } else if (!nearCenter && group.userData.videoPlaying) {
          pauseVideo(group.userData.videoUrl);
          group.userData.videoPlaying = false;
        }
      }
      for (let c = 0; c < group.children.length; c++) {
        const child = group.children[c];
        const u = child && child.material && child.material.uniforms;
        if (!u) continue;
        if (u.uEdgeVelocity && u.uEdgeVelocity.value)
          u.uEdgeVelocity.value.set(0.0, group.userData.edgeVelY * 1.0);
        if (u.uScrollOffset) u.uScrollOffset.value = currentDelta;
        if (u.uMovementStrength)
          u.uMovementStrength.value +=
            (movement - u.uMovementStrength.value) * 0.1;
        if (u.uPlaneY) u.uPlaneY.value = group.position.y;
        if (u.uTopLine) u.uTopLine.value = topLine;
        if (u.uBottomLine) u.uBottomLine.value = bottomLine;
        if (u.uMousePos && u.uMousePos.value)
          u.uMousePos.value.copy(group.userData.currentMouse);
        if (u.uHover) u.uHover.value = group.userData.hoverVal;
        if (u.uTime) u.uTime.value = elapsed;
        if (u.uOpacity) u.uOpacity.value = carouselOpacity;
      }
    }
    for (let i = 0; i < planes.length; i++) {
      const group = planes[i];
      if (!group || !group.children[0]) continue;
      const u = group.children[0].material.uniforms;
      if (!u.uEntryProgress) u.uEntryProgress = { value: 0 };
    }
    if (galleryOpacity > 0.01) {
      galleryGroup.visible = true;
      const count = galleryGroup.children.length;
      if (count > 0) {
        const firstBaseX = (0 - Math.floor(count / 2)) * GALLERY_PLANE_GAP;
        const lastBaseX =
          (count - 1 - Math.floor(count / 2)) * GALLERY_PLANE_GAP;
        const scrollMargin = isMobile ? 0.8 : 0;
        const minLimit = firstBaseX - scrollMargin;
        const maxLimit = lastBaseX + scrollMargin;
        targetX = Math.max(minLimit, Math.min(maxLimit, targetX));
        currentX += (targetX - currentX) * 0.1;
        targetDeltaX *= 0.9;
        currentDeltaX += (targetDeltaX - currentDeltaX) * 0.1;
        for (let i = 0; i < count; i++) {
          const mesh = galleryGroup.children[i];
          const baseX = (i - Math.floor(count / 2)) * GALLERY_PLANE_GAP;
          mesh.position.x = baseX - currentX;
          mesh.position.y = 0;
          const u = mesh.material && mesh.material.uniforms;
          if (u) {
            if (u.uTime) u.uTime.value = elapsed;
            if (u.uOpacity) u.uOpacity.value = galleryOpacity;
            if (u.uScrollOffset) u.uScrollOffset.value = currentDeltaX;
          }
        }
      }
    } else {
      galleryGroup.visible = false;
    }
  }

  const dummyRT = new THREE.WebGLRenderTarget(1, 1);

  function renderForSnapshot(rendererRef, targetRT, cam = camera) {
    update(1 / 60);
    const prevRT = rendererRef.getRenderTarget();

    rendererRef.setRenderTarget(dummyRT);
    rendererRef.clear();

    if (composer) {
      const prevRenderToScreen = composer.renderToScreen;
      composer.renderToScreen = false;
      rendererRef.setRenderTarget(targetRT);
      rendererRef.clear(true, true, true);
      composer.render();
      composer.renderToScreen = prevRenderToScreen;
    } else {
      rendererRef.setRenderTarget(targetRT);
      rendererRef.clear(true, true, true);
      rendererRef.render(scene, cam);
    }
    rendererRef.setRenderTarget(prevRT);
  }

  function animateCarouselEntryDrop(duration = 2.5) {
    planes.forEach((group) => {
      const mesh = group.children[0];

      if (!mesh || !mesh.material.uniforms.uEntryProgress) return;

      mesh.material.uniforms.uEntryProgress.value = 0;

      gsap.to(mesh.material.uniforms.uEntryProgress, {
        value: 1.0,
        duration: duration,
        ease: "power2.out",
        overwrite: true,
      });
    });
  }

  function animateCarouselExitRise(duration = 1.6) {
    return new Promise((resolve) => {
      let completed = 0;
      const total = planes.length;

      if (total === 0) {
        resolve();
        return;
      }

      planes.forEach((group, idx) => {
        const mesh = group.children[0];
        if (!mesh || !mesh.material.uniforms.uEntryProgress) {
          completed++;
          if (completed === total) resolve();
          return;
        }

        const centerOffset = Math.abs(idx - Math.floor(total / 2));
        const delay = centerOffset * 0.06;

        gsap.to(mesh.material.uniforms.uEntryProgress, {
          value: 0.0,
          duration: duration,
          delay: delay,
          ease: "power2.in",
          overwrite: true,
          onComplete: () => {
            completed++;
            if (completed === total) resolve();
          },
        });
      });
    });
  }

  const eventOptions = { passive: true };
  function bindEvents() {
    window.addEventListener(
      "mousemove",
      onPointerMoveForVelocity,
      eventOptions,
    );
    window.addEventListener("pointermove", onPointerMove, eventOptions);
    window.addEventListener("wheel", onWheel, eventOptions);
    window.addEventListener("touchstart", onTouchStart, eventOptions);
    window.addEventListener("touchmove", onTouchMove, eventOptions);
    window.addEventListener("click", onClick, eventOptions);
  }
  function unbindEvents() {
    window.removeEventListener("mousemove", onPointerMoveForVelocity);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("wheel", onWheel);
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("click", onClick);
  }

  let shouldResetCarousel = false;

  function activate() {
    window.__galleryActive = true;
    window.setCarouselActive && window.setCarouselActive(true);

    for (let i = 0; i < planes.length; i++) {
      const group = planes[i];
      if (!group || !group.children[0]) continue;
      const u = group.children[0].material.uniforms;
      if (u) u.uEntryProgress = { value: 0 };
    }

    bindEvents();

    if (shouldResetCarousel) {
      targetY = 0;
      currentY = 0;
      targetDelta = 0;
      currentDelta = 0;
      shouldResetCarousel = false;
    }

    animateCarouselEntryDrop(2.5);
  }

  function deactivate() {
    window.__galleryActive = false;
    unbindEvents();
    viewState = "carousel";

    shouldResetCarousel = true;

    window.setCarouselActive && window.setCarouselActive(false);
    return animateCarouselExitRise(1.6);
  }

  function onResize(w, h) {
    const width = Math.max(1, w);
    const height = Math.max(1, h);

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    composer.setSize(width, height);
  }

  startLoading();

  return {
    scene,
    composer,
    update,
    activate,
    deactivate,
    ready,
    renderForSnapshot,
    onResize,
    hideLoader: () => {
      const el =
        typeof document !== "undefined" && document.getElementById("loader");
      if (el) el.style.display = "none";
    },
    _internal: {
      planes,
      galleryGroup,
      textureMap,
      videoElementMap,
      refreshCarouselMeshCache,
    },
  };
}
